/**
 * Context Builder
 * TASK-293: Single responsibility - Build search context from client data
 *
 * Fetches portfolio holdings and builds context for search queries
 */

import { httpClient } from '../../lib/httpClient';
import type { SearchContext, PortfolioHolding } from './types';

/**
 * Interface for portfolio service dependency (for testability)
 */
export interface IPortfolioService {
  getHoldings(clientId: string): Promise<PortfolioHolding[]>;
}

/**
 * Default implementation using httpClient
 */
const defaultPortfolioService: IPortfolioService = {
  getHoldings: async (clientId: string): Promise<PortfolioHolding[]> => {
    try {
      const response = await httpClient.get<{ positions: Array<{ symbol?: string; isin?: string; security_name?: string; market_value?: number; current_value?: number }> }>(
        `/api/portfolio/positions/${clientId}`
      );
      return (response.positions || []).map((p) => ({
        symbol: p.symbol || p.isin || '',
        name: p.security_name || p.symbol || p.isin || '',
        value: p.current_value ?? p.market_value,
      }));
    } catch (error) {
      console.error('[ContextBuilder] Failed to fetch holdings:', error);
      return [];
    }
  },
};

/**
 * Build search context from client data
 *
 * @param clientId - The client ID
 * @param clientName - The client name
 * @param portfolioService - Optional portfolio service for dependency injection
 * @returns SearchContext with client and portfolio information
 */
export async function buildSearchContext(
  clientId: string,
  clientName: string,
  portfolioService: IPortfolioService = defaultPortfolioService
): Promise<SearchContext> {
  let holdings: PortfolioHolding[] = [];
  let holdingsLoadError: string | undefined;

  try {
    holdings = await portfolioService.getHoldings(clientId);
  } catch (error) {
    holdingsLoadError = error instanceof Error ? error.message : 'Failed to load holdings';
    console.error('[ContextBuilder] Holdings fetch failed:', error);
  }

  return {
    clientId,
    clientName,
    holdingSymbols: holdings.map((h) => h.symbol).filter(Boolean),
    holdingCompanies: holdings.map((h) => h.name).filter(Boolean),
    ...(holdingsLoadError && { holdingsLoadError }),
  };
}

/**
 * Check if context has portfolio data
 */
export function hasPortfolioData(context: SearchContext): boolean {
  return context.holdingSymbols.length > 0 || context.holdingCompanies.length > 0;
}

/**
 * Get top holdings for query (limit to avoid overly long queries)
 */
export function getTopHoldings(context: SearchContext, limit: number = 5): {
  symbols: string[];
  companies: string[];
} {
  return {
    symbols: context.holdingSymbols.slice(0, limit),
    companies: context.holdingCompanies.slice(0, limit),
  };
}
